import "./NewLayout.css";
import { getLeaderSideBarItems } from "../pages/sharedPages/NewSidebar/LeaderSideBar";
import { memberSideBarItems } from "../pages/sharedPages/NewSidebar/MemberSideBar";
import { superadminSideBarItems } from "../pages/sharedPages/NewSidebar/SuperAdminSideBar";

import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
  ScrollRestoration,
  Navigate,
} from "react-router-dom";
import { setShowSidebar } from "../redux/sidebar/sidebarSlice";
import { useDispatch, useSelector } from "react-redux";
import NewSidebar from "../pages/sharedPages/NewSidebar/NewSidebar";
import ProfileDropdown from "../pages/sharedPages/ProfileDropdown/ProfileDropdown";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import SettingDropdown from "../pages/sharedPages/SettingDropdown/SettingDropdown";
import useApi from "../helpers/apiHelper/requestHelper";
import { toastify } from "../helpers/toast/toastify";
import {
  globalCurrentPageTitle,
  globalAllLeaderCommunities,
} from "../store/globalstore";
import { useAtom } from "jotai";
import {
  DashboardMarketProvider,
  shouldShowCopyTradeMarketIndicator,
  resolveCommunityRecommendedPermissionType,
  cacheRecommendedPermissionType,
} from "../pages/sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";
import {
  setOnBoardingStartContext,
  storeUser,
} from "../redux/users/usersSlice";
import {
  setRefetchMultiCommunityList,
  setRefetchPage,
  setRefetchSpecificUser,
  setCurrentCommunity,
} from "../redux/multicommunity/multicommunity";
import TutorialModal from "../pages/sharedPages/CommonComponents/TutorialModal/TutorialModal";
import Overlay from "../components/Overlay";
import SetupDropdown from "../pages/NewOnboarding/OnboardingCL/setupDropdown/SetupDropDown";
import Notifications from "../pages/sharedPages/Notifications";
import AlertsDropdown from "../pages/sharedPages/AlertsDropdown/AlertsDropdown";
import MobileCommunity from "../pages/sharedPages/CommonComponents/MobileCommunity";
import { setCurrentTab } from "../redux/links/linksSlice";

const roleSidebarMap = {
  traderOnly: getLeaderSideBarItems("traderOnly"),
  communityOnly: getLeaderSideBarItems("communityOnly"),
  traderAndCommunity: getLeaderSideBarItems("traderAndCommunity"),
  member: memberSideBarItems,
  superadmin: superadminSideBarItems,
};

// Derives sidebar items from a user object without an API call.
// Used to pre-populate the sidebar instantly on mount from Redux state.
const deriveSidebarItems = (u) => {
  if (!u?.isWatchTutorial) return [];
  if (u.role === "leader") {
    if (!u.leaderCommunity && u.tradeName) return roleSidebarMap.traderOnly;
    if (u.verifiedmyTrade?.connect) return roleSidebarMap.traderAndCommunity;
    return roleSidebarMap.communityOnly;
  }
  return roleSidebarMap[u.role || ""] || [];
};

const NewLayout = () => {
  const [searchParams] = useSearchParams();
  const [sideBarItem, setSideBarItems] = useState([]);
  const [hideButton, setHideButton] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const { user } = useSelector((state) => state.user);
  const [show, setShow] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobileClosing, setIsMobileClosing] = useState(false);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const [allLeaderCommunities, setAllLeaderCommunities] = useAtom(
    globalAllLeaderCommunities,
  );
  const [communityOverlay, setCommunityOverlay] = useState(false);
  const [traderOverlay, setTraderOverlay] = useState(false);
  const [showOverLay, setShowOverlay] = useState(false);
  const [zindex, setZIndex] = useState(false);
  const refetchList = useSelector(
    (state) => state?.multiCommunity?.isRefetchMultiCommunityList,
  );
  const refetchUser = useSelector(
    (state) => state?.multiCommunity?.isRefetchUser,
  );
  const refetchScreen = useSelector(
    (state) => state?.multiCommunity?.isRefetchFromSettingPage,
  );

  const selectedCommunity = useSelector(
    (state) => state?.multiCommunity?.currentCommunity,
  );

  const [currentPageTitle, setCurrentPageTitle] = useAtom(
    globalCurrentPageTitle,
  );

  const onBoardingStartContext = useSelector(
    (state) => state.user?.onBoardingStartContext,
  );

  const api = useApi();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const page = searchParams.get("page");
  const role = searchParams.get("role");
  const token = searchParams.get("token");

  const viewOnBlockchainParams = (
    user?.leaderCommunity?.name ? user.leaderCommunity.name : user?.tradeName
  )
    ?.split(" ")
    ?.join("_");

  // Pre-populate sidebar instantly from cached Redux user — no API wait on reload
  useEffect(() => {
    const items = deriveSidebarItems(user);
    if (items.length) setSideBarItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep Recommended market cache fresh for sidebar indicator (live CL market).
  const [, setRecommendedMarketVersion] = useState(0);
  useEffect(() => {
    if (user?.role !== "member") return;
    const communityId = user?.memberCommunities?.[0]?.community?._id;
    if (!communityId) return;

    let cancelled = false;
    const refreshRecommendedMarket = async () => {
      try {
        const response = await api("post", "NoLinkMember/allCommunities");
        const communities = response?.data?.communities || [];
        const matched = communities.find(
          (item) => String(item?._id) === String(communityId)
        );
        const recommended =
          resolveCommunityRecommendedPermissionType(matched);
        if (!cancelled && recommended) {
          cacheRecommendedPermissionType(communityId, recommended);
          setRecommendedMarketVersion((v) => v + 1);
        }
      } catch (_) {
        // Keep existing cache/snapshot if listing fails
      }
    };

    refreshRecommendedMarket();
    return () => {
      cancelled = true;
    };
  }, [
    user?.role,
    user?.memberCommunities?.[0]?.community?._id,
    user?.memberCommunities?.[0]?.community?.permissionType,
    user?.memberCommunities?.[0]?.community?.exchange,
  ]);

  useEffect(() => {
    getSpecificUser();
  }, [user?.isWatchTutorial]);
  useEffect(() => {
    if (Object.keys(user || {})?.length) {
      setShow(!user?.isWatchTutorial);
    }
  }, [user]);
  useEffect(() => {
    const fetchData = async () => {
      if (token) {
        localStorage.setItem("token", token);
        await leaderCommunityList();
        dispatch(setShowSidebar(true));
        navigate(`/${role}/${page}`);
      } else {
        await leaderCommunityList();
      }
    };

    fetchData();
  }, []);

  const leaderCommunityList = async () => {
    const response = await api("get", "multiCommunity/leaderCommunityList");
    const leaderAllCommunities = response?.data || [];
    setAllLeaderCommunities(leaderAllCommunities);

    if (leaderAllCommunities.length > 0) {
      const existingCommunity = selectedCommunity?._id
        ? leaderAllCommunities.find(
            (community) => community._id === selectedCommunity._id,
          )
        : null;

      dispatch(
        setCurrentCommunity(
          existingCommunity ||
            leaderAllCommunities[leaderAllCommunities.length - 1],
        ),
      );
    }

    dispatch(setRefetchMultiCommunityList(false));
    dispatch(setRefetchPage(""));
  };

  useEffect(() => {
    if (refetchList) leaderCommunityList();
  }, [refetchList]);

  useEffect(() => {
    leaderCommunityList();
    getSpecificUser();
  }, []);
  useEffect(() => {
    if (!user?.isWatchTutorial) {
      getSpecificUserTutorial();
    }
  }, [user?.isWatchTutorial, refetchUser]);
  const getSpecificUserTutorial = async () => {
    const response = await api("post", "multiCommunity/getSpecificUser", {
      id: selectedCommunity?.leader,
    });
    localStorage.setItem("token", response?.data?.token);
    dispatch(storeUser(response?.data));
    const user = response?.data?.user || {};
    if (user?.role === "leader") {
      if (
        user?.activeTutorialScreen.purposeOfJoining === "Verify Your Trading"
      ) {
        // show sidebar controls as trader
        setSideBarItems(roleSidebarMap.traderOnly);
        setZIndex(true);
      } else if (
        user?.activeTutorialScreen.purposeOfJoining ===
        "Launch Your Own Copy Trade"
      ) {
        setSideBarItems(roleSidebarMap.communityOnly);
        setZIndex(true);
      }
    } else if (user.role === "member") {
      setZIndex(true);
    }
    dispatch(setRefetchSpecificUser(false));
  };

  const getSpecificUser = async () => {
    const response = await api("post", "multiCommunity/getSpecificUser", {
      id: selectedCommunity?.leader,
    });

    localStorage.setItem("token", response?.data?.token);
    dispatch(storeUser(response?.data));
    const user = response?.data?.user || {};
    if (user?.role === "leader") {
      if (!user?.isWatchTutorial) {
        return;
      }
      setZIndex(false);
      if (!user.leaderCommunity && user.tradeName) {
        // show sidebar controls as trader
        setSideBarItems(roleSidebarMap.traderOnly);
      } else {
        if (user?.verifiedmyTrade.connect) {
          setSideBarItems(roleSidebarMap.traderAndCommunity);
          return;
        }

        setSideBarItems(roleSidebarMap.communityOnly);
        if (
          location.pathname.includes("verifiedTrading") &&
          !user?.verifiedmyTrade?.connect
        ) {
          navigate("/leader/dashboard");
        }
        if (
          location.pathname.includes("verifiedPaymentBillingHistory") &&
          !user?.verifiedmyTrade?.subscription?.subscriptionId
        ) {
          navigate("/leader/dashboard");
        }
        // if (
        //   user?.verifiedmyTrade?.payment ||
        //   user?.verifiedmyTrade?.subscription?.status === "active" ||
        //   user?.verifiedmyTrade?.subscription?.status === "canceled"
        // ) {
        //   // show all sidebar controls as trader and community both
        //   setSideBarItems(roleSidebarMap.traderAndCommunity);
        // } else {
        //   // show sidebar controls as community
        //   setSideBarItems(roleSidebarMap.communityOnly);
        // }
      }
    } else {
      if (!user?.isWatchTutorial) {
        return;
      }
      setZIndex(false);
      // show sidebar controls as member or superadmin according to role
      setSideBarItems(roleSidebarMap[user?.role || ""] || []);
    }
    dispatch(setRefetchSpecificUser(false));
  };

  useEffect(() => {
    if (selectedCommunity) getSpecificUser();
  }, [selectedCommunity, location.pathname]);

  useEffect(() => {
    if (refetchUser) getSpecificUser();
  }, [refetchUser]);

  useEffect(() => {
    let isOnBoardingComplete;
    let isTraderOnboardingConnected;
    setShowOverlay(false);
    if (user?.role === "member") {
      isOnBoardingComplete =
        user?.onboarding?.connect && user?.onboarding?.payment;
      if (
        location.pathname === "/member/copyTrade" ||
        location.pathname.includes("stripePaymentScreen")
      ) {
        if (isOnBoardingComplete) setHideButton(true);

        return;
      }
    } else if (user?.role === "leader" && !user?.tradeName) {
      isOnBoardingComplete =
        user?.onboarding?.isCommunityCreated &&
        user?.onboarding?.connect &&
        user?.onboarding?.payment &&
        user?.onboarding?.subscribed &&
        user?.onboarding?.isVerified;
    } else if (user?.role === "leader" && user?.tradeName) {
      isTraderOnboardingConnected = user?.onboarding?.connect;
    }

    if (user?.role === "member" && isOnBoardingComplete) {
      setHideButton(true);
    }
    const pathSegments = location.pathname.split("/");
    const secondSegment = pathSegments[2];

    const verifyTradeRoutes = [
      "dashboard",
      "verifyMyTradeDashboard",
      "verifiedTrading",
      "verifyTradeListing",
      "verifiedPaymentBillingHistory",
    ];
    if (
      !isOnBoardingComplete &&
      !isTraderOnboardingConnected &&
      secondSegment !== "dashboard" &&
      secondSegment !== "onBoardingNotComplete" &&
      secondSegment !== "onboarding" &&
      !verifyTradeRoutes.includes(secondSegment)
    ) {
      setShowOverlay(true);
    }
    setTraderOverlay(
      user?.tradeName &&
        user?.role === "leader" &&
        !user?.leaderCommunity &&
        !user?.verifiedmyTrade?.payment &&
        !user?.verifiedmyTrade?.subscription?.subscriptionId,
    );
    setCommunityOverlay(
      user?.role === "leader" &&
        (!user?.onboarding.isCommunityCreated ||
          !user?.onboarding.subscribed ||
          !user?.onboarding.connect ||
          !user?.onboarding.payment ||
          !user?.onboarding.isVerified),
    );
  }, [location.pathname, user]);
  useEffect(() => {
    if (user) {
      if (user?.role === "leader") {
        if (
          allLeaderCommunities?.length <= 1 &&
          (!user?.onboarding.isCommunityCreated ||
            !user?.onboarding.subscribed ||
            !user?.onboarding.connect ||
            !user?.onboarding.payment ||
            !user?.onboarding.isVerified)
        ) {
          setButtonText("Start");
        } else {
          setButtonText("+ Create");
        }
      } else {
        setButtonText("Start");
      }
    }
  }, [user]);

  const overlayStyling = {
    position: "relative",
    zIndex: 9999,
  };

  // Mobile menu handlers
  const closeMobileMenu = () => {
    setIsMobileClosing(true);
    setIsMobileSidebarOpen(false);
  };

  const handleMobileLinkClick = (path) => {
    setIsMobileSidebarOpen(false);
    setIsMobileClosing(false);
    setMobileOpenDropdown(null);
    navigate(path);
  };

  const handleMobileOverlayAnimationEnd = () => {
    if (isMobileClosing) {
      setIsMobileClosing(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = (isMobileSidebarOpen || isMobileClosing) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobileSidebarOpen, isMobileClosing]);

  // function shouldShowWarning() {
  //   const targetRoutes = [
  //     "/leader/dashboard",
  //     "/leader/verifyMyTradeDashboard",
  //     "/leader/verifiedTrading",
  //     "/leader/verifyTradeListing",
  //     "/leader/verifiedPaymentBillingHistory",
  //   ];

  //   const isMissingConnection =
  //     !user?.bybitCredentials?.isConnected &&
  //     !user?.binanceCredentials?.isConnected;

  //   const isInTargetRoute = targetRoutes.includes(location.pathname);

  //   return isMissingConnection && isInTargetRoute;
  // }

  const verifyTradeListingClassVar =
    location.pathname === "/leader/verifyTradeListing";

  const verifiedPaymentBillingHistory =
    location.pathname === "/leader/verifiedPaymentBillingHistory";

  let filteredSidebarkeys;
  if (user?.role === "leader") {
    if (user?.verifiedmyTrade?.payment) {
      filteredSidebarkeys = sideBarItem;
    } else {
      filteredSidebarkeys = sideBarItem.filter(
        (item) => item.key !== "payment",
      );
    }
  } else if (user?.role === "member") {
    // OLD: hid Market Place for memberNoLink users once they subscribed
    // if (user?.memberNoLink && user?.memberCommunities?.[0]?.subscribed) {
    //   filteredSidebarkeys = sideBarItem.filter(
    //     (item) => item.label !== "Market Place",
    //   );
    // } else if (!user?.memberNoLink) {
    if (!user?.memberNoLink) {
      filteredSidebarkeys = sideBarItem.filter(
        (item) => item.label !== "Market Place",
      );
    } else {
      filteredSidebarkeys = sideBarItem;
    }
  }

  const showCopyTradeMarketIndicator = shouldShowCopyTradeMarketIndicator(user);
  if (showCopyTradeMarketIndicator && Array.isArray(filteredSidebarkeys)) {
    filteredSidebarkeys = filteredSidebarkeys.map((item) =>
      item?.url === "/member/copyTrade2" ||
      item?.label === "Copy Trade Settings"
        ? { ...item, showIndicator: true }
        : item
    );
  }

  // const filteredSidebarkeys = user?.verifiedmyTrade?.payment? sideBarItem: sideBarItem.filter((item) => item.key !== "payment");

  // Block cross-role access before rendering the wrong dashboard/shell
  if (user?.role === "member" && location.pathname.startsWith("/leader")) {
    return <Navigate to="/member/dashboard" replace />;
  }
  if (user?.role === "leader" && location.pathname.startsWith("/member")) {
    return <Navigate to="/leader/dashboard" replace />;
  }

  return (
    <div className="newLayoutBox">
      <div
        className={`sideBarBox ${!sideBarItem.length && "d-none"} ${
          (zindex || (user?.role === "member"&& !user?.isWatchTutorial)) && "customSideBar"
        }`}
      >
        <NewSidebar
          items={filteredSidebarkeys}
          communities={allLeaderCommunities}
          isOpen={isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
        />
      </div>
      <DashboardMarketProvider forceEnable={/\/tradingListening\/?$/.test(location.pathname)}>
      <div className={`mainContentBox`}>
        {/* Mobile sticky header */}
        <div className="mobileHeader">
          <MobileCommunity communities={allLeaderCommunities} />
          <div className="d-flex gap-2">
            <div className="settingProfileBox"
              style={{
                ...(traderOverlay ? overlayStyling : {}),
                ...(communityOverlay &&
                (user?.leaderCommunity ||
                  (!user?.tradeName && !user?.leaderCommunity))
                  ? overlayStyling
                  : {}),
                ...(user?.role === "member" &&
                (!user?.onboarding?.connect || !user?.onboarding?.payment)
                  ? overlayStyling
                  : {}),
              }}
            >
              {!hideButton && <SetupDropdown buttonText={buttonText} />}
            </div>

            <div className="settingProfileBox">
              {/* Should not appear in the mobile header
              {showDashboardMarketSelect && <DashboardMarketSelect />}
              */}
              <AlertsDropdown />
              <Notifications />
              <button
                className="mobileHamburger"
                onClick={() => setIsMobileSidebarOpen(true)}
                type="button"
              >
                <span></span>
              </button>
            </div>
          </div>
        </div>

        {/* Add full page Overlay if onboarding is not completed or any wallet is not connected */}
        {traderOverlay && <Overlay />}
        {communityOverlay &&
          (user?.leaderCommunity ||
            (!user?.tradeName && !user?.leaderCommunity)) && <Overlay />}
        {user?.role === "member" &&
          // !user?.memberCommunities?.[0]?.subscription &&
          (!user?.onboarding?.connect || !user?.onboarding?.payment) && (
            <Overlay />
          )}

        {/* location.pathname !== "/member/stripePaymentScreen" && */}
        {/* location.pathname !== "/member/copyTrade" && */}
        <div className={`container-fluid g-0`}>
          {/* <Pagination/>  */}
          <div
            className={`layoutTitleBox ${
              verifiedPaymentBillingHistory && "paymentLayoutTitleBox"
            }`}
          >
            <div
              className="d-flex justify-content-md-between justify-content-center flex-md-row flex-column-reverse"
              style={{ columnGap: 20, rowGap: 30 }}
            >
              <div
                className=""
                // "col-lg-4 col-md-4 col-12 order-lg-1 order-md-1 order-2"
              >
                <div className="layoutTitleBox__titleRow">
                  <h2
                    className={`Pagetitle ${
                      verifyTradeListingClassVar ? "verifyTradingTitle" : ""
                    }`}
                  >
                    {show ? "Dashboard" : currentPageTitle}
                  </h2>
                </div>
              </div>
              <div className="desktopOnly">
                <div className="viewBlockchainBtnBox">
                  <div
                    className="settingProfileBox"
                    style={{
                      ...(traderOverlay ? overlayStyling : {}),
                      ...(communityOverlay &&
                      (user?.leaderCommunity ||
                        (!user?.tradeName && !user?.leaderCommunity))
                        ? overlayStyling
                        : {}),
                      ...(user?.role === "member" &&
                      (!user?.onboarding?.connect || !user?.onboarding?.payment)
                        ? overlayStyling
                        : {}),
                    }}
                  >
                    {!hideButton && <SetupDropdown buttonText={buttonText} />}
                    <AlertsDropdown />
                    <Notifications />
                    <ProfileDropdown />
                    <SettingDropdown />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Outlet />
        <ScrollRestoration />
      </div>
      </DashboardMarketProvider>
      {show && <TutorialModal />}

      {/* ── Mobile overlay portal — rendered directly here so state is never out of sync ── */}
      {(isMobileSidebarOpen || isMobileClosing) && ReactDOM.createPortal(
        <div
          className={`mobile-menu__overlay${isMobileClosing ? " closing" : ""}`}
          onAnimationEnd={handleMobileOverlayAnimationEnd}
        >
          <div className={`dropdown_box${isMobileClosing ? " closing" : ""}`}>
            <button className="mobile-menu__close" onClick={closeMobileMenu}>&times;</button>
            <nav className="mobile-menu__nav">
              {filteredSidebarkeys?.map((item, index) => (
                <div key={index}>
                  {!item.isDropDown ? (
                    <button
                      className={`mobile-menu__item${location.pathname === item.url ? " mobile-menu__item--active" : ""}`}
                      onClick={() => {
                        dispatch(setCurrentTab("subscribe"));
                        if (item.currentPageTitle) setCurrentPageTitle(item.currentPageTitle);
                        handleMobileLinkClick(item.url);
                      }}
                    >
                      {item.label}
                      {item.showIndicator && (
                        <span
                          className="mobile-menu__indicator"
                          aria-label="Action needed"
                        />
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        className="mobile-menu__item mobile-menu__item--dropdown"
                        onClick={() => setMobileOpenDropdown(mobileOpenDropdown === index ? null : index)}
                      >
                        {item.label}
                        <svg
                          className={`mobile-menu__chevron${mobileOpenDropdown === index ? " mobile-menu__chevron--open" : ""}`}
                          viewBox="0 0 10 6" fill="none"
                        >
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {mobileOpenDropdown === index && (
                        <div className="mobile-menu__subnav">
                          {item.subItems?.map((subItem, subIndex) => (
                            <button
                              key={subIndex}
                              className="mobile-menu__subitem"
                              onClick={() => {
                                setCurrentPageTitle(subItem.currentPageTitle);
                                handleMobileLinkClick(subItem.url);
                              }}
                            >
                              {subItem.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {user?.role === "leader" &&
                user?.onboarding?.isCommunityCreated &&
                user?.onboarding?.connect &&
                user?.onboarding?.payment &&
                user?.onboarding?.subscribed &&
                user?.onboarding?.isVerified && (
                  <button
                    className="mobile-menu__item"
                    onClick={() => {
                      const communityLink = `${document.location.origin}/community/${user?.leaderCommunity?.name?.split(" ").join("")}`;
                      navigator.clipboard
                        .writeText(communityLink)
                        .then(() => toastify("Community link copied to clipboard!", "success"))
                        .catch(() => toastify("Failed to copy community link.", "error"));
                      closeMobileMenu();
                    }}
                  >
                    Copy Trade URL
                  </button>
                )}
              <button
                className="mobile-menu__item"
                onClick={() => handleMobileLinkClick(`/${user?.role}/support`)}
              >
                Support
              </button>
              <button
                className="mobile-menu__item mobile-menu__item--logout"
                onClick={() => handleMobileLinkClick(`/${user?.role}/logout`)}
              >
                LOG OUT
              </button>
            </nav>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NewLayout;
